import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFWmsEntities,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withArraySearchFilter,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withFragmentSearchFilter,
    withMetadata,
    withSearchFilter,
    withSearchFilterArrayUnTyped,
    withSearchFilterUnTyped,
} from '@pf/shared-utility';
import {
    AsnHeaderCreate,
    AsnHeaderDto,
    AsnHeaderRead,
    AsnHeaderSearchParams,
} from '../../entities/asn-header.dto';
import {
    CustomFieldMetadataMap,
    ResourceAuthSymbol,
} from '@pf/shared/util-platform';
import { AsnLineMetadataMap } from '../asn-lines/asn-line.mapping-profile';

const symbols = entitySymbols(PFWmsEntities.AsnHeader);

withDefaultEntityMetadata<AsnHeaderDto>(symbols.dto, {
    billOfLading: String,
    resourceAuth: ResourceAuthSymbol,
    externalReferenceId: String,
    status: String,
    shipDateTime: String,
    vendor: Object,
    carrier: Object,
    originLocation: Object,
    destinationLocation: Object,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    lines: [AsnLineMetadataMap.symbols.dto],
    trackingNumber: String,
    expectedDateTime: String,
});

withDefaultEntityMetadata<AsnHeaderRead>(symbols.entity, {
    billOfLading: String,
    resourceAuth: ResourceAuthSymbol,
    externalReferenceId: String,
    status: String,
    shipDateTime: String,
    vendor: Object,
    carrier: Object,
    originLocation: Object,
    destinationLocation: Object,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    lines: [AsnLineMetadataMap.symbols.entity],
    trackingNumber: String,
    expectedDateTime: String,
});

withMetadata<AsnHeaderCreate>(symbols.createBody, {
    billOfLading: String,
    carrierId: String,
    destinationLocationId: String,
    externalReferenceId: String,
    originLocationId: String,
    resourceAuth: ResourceAuthSymbol,
    shipDateTime: String,
    status: String,
    vendorId: String,
    trackingNumber: String,
    expectedDateTime: String,
});

withDefaultSearchParamsMetadata<AsnHeaderSearchParams>(symbols.searchParams, {
    externalReferenceId: String,
    purchaseOrderId: String,
    trackingNumber: String,
    vendorIds: [String],
    itemIds: [String],
    status: [String],
    originLocationId: String,
    destinationLocationId: String,
    expectedDateStartTime: String,
    expectedDateEndTime: String,
});

export const AsnHeaderMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<AsnHeaderRead, AsnHeaderDto>(
            mapper,
            symbols.entity,
            symbols.dto
        );
        createMap<AsnHeaderDto, AsnHeaderRead>(
            mapper,
            symbols.dto,
            symbols.entity
        );
        createMap<AsnHeaderDto, AsnHeaderCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                d => d.vendorId,
                mapFrom(s => s.vendor?.id)
            ),
            forMember(
                d => d.carrierId,
                mapFrom(s => s.carrier?.id)
            ),
            forMember(
                d => d.destinationLocationId,
                mapFrom(s => s.destinationLocation?.id)
            ),
            forMember(
                d => d.originLocationId,
                mapFrom(s => s.originLocation?.id)
            )
        );
        createMap<IQueryParams<AsnHeaderDto>, AsnHeaderSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            withSearchFilter('externalReferenceId'),
            withSearchFilter('trackingNumber'),
            withArraySearchFilter('status'),
            withFragmentSearchFilter('destinationLocation'),
            withFragmentSearchFilter('originLocation'),
            withSearchFilterUnTyped('purchaseOrderId'),
            withSearchFilterArrayUnTyped('vendorIds'),
            withSearchFilterArrayUnTyped('itemIds'),
            forMember(
                d => d.expectedDateStartTime,
                mapFrom(
                    s =>
                        (s.filters as Record<string, unknown>)?.[
                            'expectedDateStartTime'
                        ]
                )
            ),
            forMember(
                d => d.expectedDateEndTime,
                mapFrom(
                    s =>
                        (s.filters as Record<string, unknown>)?.[
                            'expectedDateEndTime'
                        ] ?? s.filters?.expectedDateTime
                )
            ),
            ...withDefaultSearchParamsMappingConfiguration()
        );
    },
};
